import { useState, useEffect } from 'react';
import { ExclamationTriangleIcon, BugAntIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { submitProblemReport } from '@/services/report-problem.service';
import toast from 'react-hot-toast';

export default function ReportProblemPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    description: '',
    honeypot: '' // Anti-spam honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedIssueSummary, setSubmittedIssueSummary] = useState('');

  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSubmitted]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Submit form to backend
      await submitProblemReport(formData);

      // Store the issue summary before resetting form
      setSubmittedIssueSummary(formData.subject);

      // Show success message
      toast.success('Problem report submitted! Our team will investigate this issue.');

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        description: '',
        honeypot: ''
      });

      setIsSubmitted(true);

    } catch (error: any) {
      console.error('Problem report submission error:', error);

      // Show error message
      const errorMessage = error.response?.data?.detail ||
                          'Failed to submit problem report. Please try again or email us directly.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Report a Problem
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Found a bug or experiencing an issue? Let us know and we'll investigate
            right away. Your feedback helps us improve PermitBuddy for everyone.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Help Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Reporting Guidelines
                </h2>
                <p className="text-gray-600 mb-6">
                  Help us resolve your issue quickly by providing detailed information.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BugAntIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Technical Issues</h3>
                    <p className="text-sm text-gray-600">
                      Bugs, errors, performance problems, or unexpected behavior
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">What to Include</h3>
                    <p className="text-sm text-gray-600">
                      Steps to reproduce, what you expected, and what actually happened
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ClockIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Response Time</h3>
                    <p className="text-sm text-gray-600">
                      We investigate all reports within 1-2 business days
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-2">Need Immediate Help?</h3>
                <p className="text-gray-600 text-sm mb-2">
                  For urgent issues or general questions, please use our{' '}
                  <a href="/contact" className="text-primary-600 hover:text-primary-700 underline">
                    Contact Us
                  </a>{' '}
                  page instead.
                </p>
                <p className="text-gray-600 text-sm">
                  Email:{' '}
                  <a
                    href="mailto:support@permitbuddy.com"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    support@permitbuddy.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Problem Report Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="input w-full"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="input w-full"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Issue Summary *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="Brief description of the problem"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Example: "Error when calculating fees for Chicago" or "Dashboard not loading"
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Problem Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows={8}
                    minLength={10}
                    value={formData.description}
                    onChange={handleChange}
                    className="input w-full resize-none"
                    placeholder="Please provide detailed information:&#10;&#10;1. What were you trying to do?&#10;2. What did you expect to happen?&#10;3. What actually happened?&#10;4. Any error messages you saw?&#10;5. Steps to reproduce the issue (if applicable)"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum 10 characters. The more details you provide, the faster we can resolve the issue.
                  </p>
                </div>

                {/* Honeypot field for spam prevention - hidden from humans */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="honeypot">Leave this field empty</label>
                  <input
                    type="text"
                    id="honeypot"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {isSubmitted && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => setIsSubmitted(false)}
                      className="absolute top-2 right-2 text-green-600 hover:text-green-800 transition-colors"
                      aria-label="Dismiss notification"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                    <div className="pr-6">
                      <p className="text-sm text-green-800 font-semibold mb-1">
                        ✓ Problem report submitted!
                      </p>
                      <p className="text-sm text-green-700 mb-2">
                        <strong>Issue:</strong> {submittedIssueSummary}
                      </p>
                      <p className="text-sm text-green-700">
                        Our engineering team will investigate this issue and get back to you soon.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-success hover:bg-success-dark text-white font-semibold px-8 py-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
            Tips for Effective Bug Reports
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                Be Specific
              </h3>
              <p className="text-gray-600">
                Instead of "It's broken," try "Error message appears when I click 'Calculate Fees' for Chicago permits."
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                Include Steps to Reproduce
              </h3>
              <p className="text-gray-600">
                List the exact steps you took before encountering the issue. This helps us recreate and fix it faster.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                Copy Error Messages
              </h3>
              <p className="text-gray-600">
                If you see an error message, copy the exact text. This helps us identify the root cause quickly.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                Note When It Started
              </h3>
              <p className="text-gray-600">
                Let us know if this is a new issue or if it's been happening consistently. Did it work before?
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
